import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { cn } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import { createVaultItem, homeExpenseQueryKeys } from "@apps/dashboard/lib/home-expense-api";
import { FilePondStorageUpload } from "@apps/dashboard/components/FilePondStorageUpload";
import { VAULT_CATEGORY_SLUGS } from "@apps/dashboard/lib/vault-ui";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { Icon } from "@project/ui";

const fieldClass =
  "mt-2 w-full rounded-xl border-none bg-surface-container-low px-4 py-4 font-body text-on-surface outline-none transition-all duration-300 focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary";

const labelClass =
  "ml-1 block text-[0.7rem] font-bold uppercase tracking-widest text-on-surface-variant";

export default function VaultNewPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { houses } = useActiveHouse();
  const actorRole = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  const canEdit = actorRole === "owner" || actorRole === "admin";
  useSyncHouseFromParams(houseId);

  const [name, setName] = useState("");
  const [assetTypeSlug, setAssetTypeSlug] = useState("");
  const [price, setPrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [warrantyEndsOn, setWarrantyEndsOn] = useState("");
  const [notes, setNotes] = useState("");
  const [documentStorageKey, setDocumentStorageKey] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const completionTotal = 5;
  let completionFilled = 0;
  if (name.trim()) completionFilled += 1;
  if (assetTypeSlug) completionFilled += 1;
  if (purchaseDate) completionFilled += 1;
  if (warrantyEndsOn) completionFilled += 1;
  if (documentStorageKey.trim()) completionFilled += 1;
  const completionPct = Math.round((completionFilled / completionTotal) * 100);

  const createMutation = useMutation({
    mutationFn: () => {
      if (!houseId) throw new Error("missing houseId");
      const priceLine = price.trim()
        ? t("dashboardUi.vault.priceInNotesLine", { amount: price.trim() })
        : "";
      const mergedNotes = [priceLine, notes.trim()].filter(Boolean).join("\n\n") || null;
      return createVaultItem(locale, houseId, {
        name: name.trim(),
        assetType: assetTypeSlug || null,
        purchaseDate: purchaseDate || null,
        warrantyEndsOn: warrantyEndsOn || null,
        notes: mergedNotes,
        documentStorageKey: documentStorageKey.trim() || null,
      });
    },
    onSuccess: async (row) => {
      if (houseId) {
        await queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.vault(locale, houseId) });
      }
      navigate(dashboardPaths.houseVaultDetail(houseId!, row.id), { replace: true });
    },
  });

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <DashboardBackLink to={dashboardPaths.houseVault(houseId)}>{t("dashboardUi.vault.backToList")}</DashboardBackLink>
        <p className="text-body-md text-on-surface-variant">{t("dashboardUi.vault.readOnlyHint")}</p>
      </div>
    );
  }

  return (
    <div className={cn(DASHBOARD_PAGE_CONTAINER, "space-y-8")}>
      <DashboardBackLink to={dashboardPaths.houseVault(houseId)}>{t("dashboardUi.vault.backToList")}</DashboardBackLink>
      <h1 className="font-headline text-headline-sm font-bold text-primary">{t("dashboardUi.pages.vaultNew")}</h1>

      <div className="mb-2">
        <span className="mb-2 block text-[0.75rem] font-bold uppercase tracking-widest text-primary">
          {t("dashboardUi.vault.formKicker")}
        </span>
        <h2 className="font-headline text-[1.5rem] font-bold tracking-tight text-on-surface">
          {t("dashboardUi.vault.formTitle")}
        </h2>
        <p className="mt-2 text-body-md text-on-surface-variant">{t("dashboardUi.vault.formSubtitle")}</p>
      </div>

      <form
        className="space-y-8"
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!name.trim()) {
            setFormError(t("validation.required"));
            return;
          }
          void createMutation.mutateAsync().catch((err: unknown) => {
            setFormError(err instanceof Error ? err.message : t("dashboardUi.states.errorHint"));
          });
        }}
      >
        <div>
          <label className={labelClass} htmlFor="vault-name">
            {t("dashboardUi.vault.nameLabel")}
          </label>
          <input
            id="vault-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("dashboardUi.vault.namePlaceholder")}
            className={fieldClass}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-5 md:gap-6">
          <div className="md:col-span-3">
            <label className={labelClass} htmlFor="vault-category">
              {t("dashboardUi.vault.assetTypeLabel")}
            </label>
            <div className="relative mt-2">
              <select
                id="vault-category"
                value={assetTypeSlug}
                onChange={(e) => setAssetTypeSlug(e.target.value)}
                className={`${fieldClass} mt-0 appearance-none pr-12`}
              >
                <option value="">{t("dashboardUi.vault.categoryPlaceholder")}</option>
                {VAULT_CATEGORY_SLUGS.map((slug) => (
                  <option key={slug} value={slug}>
                    {t(`dashboardUi.vault.categories.${slug}`)}
                  </option>
                ))}
              </select>
              <Icon
                name="ChevronDown"
                className="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 text-on-surface-variant"
                strokeWidth={1.5}
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className={labelClass} htmlFor="vault-price">
              {t("dashboardUi.vault.priceLabel")}
            </label>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-bold text-on-surface-variant">
                $
              </span>
              <input
                id="vault-price"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={t("dashboardUi.vault.pricePlaceholder")}
                className={`${fieldClass} mt-0 pl-8`}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className={labelClass} htmlFor="vault-purchase">
              {t("dashboardUi.vault.purchaseDateLabel")}
            </label>
            <input
              id="vault-purchase"
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              className={fieldClass}
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="vault-warranty">
              {t("dashboardUi.vault.warrantyLabel")}
            </label>
            <div className="relative mt-2">
              <input
                id="vault-warranty"
                type="date"
                value={warrantyEndsOn}
                onChange={(e) => setWarrantyEndsOn(e.target.value)}
                className={`${fieldClass} mt-0 pr-12`}
              />
              <Icon
                name="Timer"
                className="pointer-events-none absolute right-4 top-1/2 size-5 -translate-y-1/2 text-on-surface-variant"
                strokeWidth={1.5}
              />
            </div>
          </div>
        </div>

        <div>
          <span className={`${labelClass} mb-4`}>{t("dashboardUi.vault.documentUploadTitle")}</span>
          <div className="rounded-xl border-2 border-dashed border-outline-variant bg-surface-container-low p-4 transition-colors duration-300 focus-within:border-primary">
            <div className="mb-3 flex flex-col items-center text-center md:flex-row md:justify-between md:text-left">
              <div className="mb-3 flex size-14 shrink-0 items-center justify-center rounded-full bg-primary/10 md:mb-0 md:mr-4">
                <Icon name="Camera" className="size-7 text-primary" strokeWidth={1.5} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-body-md font-bold text-on-surface">
                  {documentStorageKey ? t("dashboardUi.vault.documentUploadedHint") : t("dashboardUi.vault.documentUploadHint")}
                </p>
                <p className="mt-1 text-[0.75rem] text-on-surface-variant">
                  {documentStorageKey ? documentStorageKey.split("/").pop() ?? "" : t("dashboardUi.vault.documentUploadSub")}
                </p>
              </div>
            </div>
            <FilePondStorageUpload
              locale={locale}
              mode="vault"
              maxFiles={1}
              inputId="vault-document-filepond"
              className="filepond-vault-upload w-full"
              onStoredKeys={(keys) => {
                const k = keys[keys.length - 1];
                if (k) {
                  setFormError(null);
                  setDocumentStorageKey(k);
                }
              }}
            />
          </div>
        </div>

        <div>
          <label className={labelClass} htmlFor="vault-notes">
            {t("dashboardUi.vault.notesLabel")}
            <span className="ml-2 font-normal normal-case tracking-normal text-on-surface-variant/80">
              ({t("dashboardUi.vault.notesOptionalHint")})
            </span>
          </label>
          <textarea
            id="vault-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("dashboardUi.vault.notesPlaceholder")}
            rows={3}
            className={fieldClass}
          />
        </div>

        <div className="pt-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[0.65rem] font-bold uppercase tracking-tighter text-on-surface-variant">
              {t("dashboardUi.vault.completionLabel")}
            </span>
            <span className="text-[0.65rem] font-bold text-primary">{completionPct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-surface-container-highest">
            <div
              className="relative h-full bg-gradient-to-r from-primary to-primary-container transition-all duration-500"
              style={{ width: `${completionPct}%` }}
            >
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "linear-gradient(45deg,rgba(255,255,255,0.4) 25%,transparent 25%,transparent 50%,rgba(255,255,255,0.4) 50%,rgba(255,255,255,0.4) 75%,transparent 75%,transparent)",
                  backgroundSize: "1rem 1rem",
                }}
              />
            </div>
          </div>
        </div>

        {formError ? <p className="text-body-md text-error">{formError}</p> : null}

        <div className="pt-2">
          <Button
            type="submit"
            disabled={createMutation.isPending}
            className="primary-gradient flex h-auto w-full items-center justify-center gap-3 rounded-xl py-5 font-headline text-lg font-bold text-on-primary shadow-lg transition-all duration-300 hover:shadow-primary/20 active:scale-[0.98]"
          >
            {createMutation.isPending ? t("dashboardUi.vault.saving") : t("dashboardUi.vault.createCta")}
            <Icon name="Wallet" className="size-5" strokeWidth={1.5} />
          </Button>
          <p className="mt-4 px-4 text-center text-[0.7rem] leading-relaxed text-on-surface-variant">
            {t("dashboardUi.vault.saveLegalNote")}
          </p>
        </div>
      </form>
    </div>
  );
}
